import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { productService, categoryService } from "../services";
import { Formik, Form } from "formik";
import { productValidationSchema } from "../utils/validationSchemas";

const flattenCategories = (nodes, level = 0) => {
  if (!Array.isArray(nodes)) return [];
  return nodes.flatMap((node) => {
    const current = {
      _id: node._id,
      title: node.title,
      level,
    };
    const children = node.subcategories
      ? flattenCategories(node.subcategories, level + 1)
      : [];
    return [current, ...children];
  });
};

const ProductForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [tagInput, setTagInput] = useState("");

  // Get product data from location state for editing
  const productData = location.state?.product || null;
  const isEdit = !!productData;

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await categoryService.getHierarchical();
        const flattened = flattenCategories(response);
        setCategoryOptions(flattened);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
        toast.error("Failed to fetch categories");
      }
    };

    loadCategories();
  }, []);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setLoading(true);

      const normalizedTags = (values.tags || [])
        .map((tag) => (typeof tag === "string" ? tag.trim() : ""))
        .filter(
          (tag, index, self) => tag.length > 0 && self.indexOf(tag) === index
        );

      const payload = {
        ...values,
        metaTitle: values.metaTitle?.trim() || "",
        metaDescription: values.metaDescription?.trim() || "",
        tags: normalizedTags,
      };

      if (isEdit) {
        await productService.updateProduct(productData._id, payload);
        toast.success("Product updated successfully");
      } else {
        await productService.createProduct(payload);
        toast.success("Product created successfully");
      }

      navigate("/products");
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          `Failed to ${isEdit ? "update" : "create"} product`
      );
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/products");
  };

  // Auto-generate product code for create flow
  const generateProductCode = () => {
    const ts = Date.now().toString().slice(-8);
    const rand = Math.floor(Math.random() * 10000)
      .toString()
      .padStart(4, "0");
    return `${ts}${rand}`;
  };

  const initialValues = useMemo(
    () => ({
      title: productData?.title || "",
      description: productData?.description || "",
      categoryId: productData?.categoryId?._id || "",
      productCode: productData?.productCode || generateProductCode(),
      isTrending: productData?.isTrending ?? false,
      metaTitle: productData?.metaTitle || "",
      metaDescription: productData?.metaDescription || "",
      tags: Array.isArray(productData?.tags) ? productData.tags : [],
    }),
    [productData]
  );

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleCancel}
          sx={{ mr: 2 }}
        >
          Back to Products
        </Button>
        <Typography variant="h4">
          {isEdit ? "Edit Product" : "Create New Product"}
        </Typography>
      </Box>

      <Paper sx={{ p: 4 }}>
        <Formik
          initialValues={initialValues}
          validationSchema={productValidationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
            isSubmitting,
          }) => {
            const tagError =
              touched.tags && typeof errors.tags === "string"
                ? errors.tags
                : undefined;

            const handleTagAdd = () => {
              const trimmed = tagInput.trim();
              if (!trimmed) {
                return;
              }

              const currentTags = Array.isArray(values.tags) ? values.tags : [];
              const exists = currentTags.some(
                (tag) => tag.toLowerCase() === trimmed.toLowerCase()
              );

              if (exists) {
                setTagInput("");
                return;
              }

              setFieldValue("tags", [...currentTags, trimmed]);
              setTagInput("");
            };

            const handleTagDelete = (tagToRemove) => {
              const currentTags = Array.isArray(values.tags) ? values.tags : [];
              setFieldValue(
                "tags",
                currentTags.filter((tag) => tag !== tagToRemove)
              );
            };

            const handleTagKeyDown = (event) => {
              if (event.key === "Enter" || event.key === ",") {
                event.preventDefault();
                handleTagAdd();
              }
            };

            return (
              <Form>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Product Title"
                      name="title"
                      value={values.title}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.title && Boolean(errors.title)}
                      helperText={touched.title && errors.title}
                      disabled={loading}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Product Code"
                      name="productCode"
                      value={values.productCode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.productCode && Boolean(errors.productCode)}
                      helperText={
                        touched.productCode &&
                        (errors.productCode ||
                          "Auto-generated and non-editable")
                      }
                      disabled
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      name="description"
                      multiline
                      rows={4}
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.description && Boolean(errors.description)}
                      helperText={touched.description && errors.description}
                      disabled={loading}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Meta Title"
                      name="metaTitle"
                      value={values.metaTitle}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.metaTitle && Boolean(errors.metaTitle)}
                      helperText={touched.metaTitle && errors.metaTitle}
                      disabled={loading}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Meta Description"
                      name="metaDescription"
                      value={values.metaDescription}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.metaDescription &&
                        Boolean(errors.metaDescription)
                      }
                      helperText={
                        touched.metaDescription && errors.metaDescription
                      }
                      disabled={loading}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Add Tag"
                      placeholder="Enter a tag and press Enter"
                      value={tagInput}
                      onChange={(event) => setTagInput(event.target.value)}
                      onKeyDown={handleTagKeyDown}
                      disabled={loading}
                    />
                    <Box mt={1} display="flex" gap={1} flexWrap="wrap">
                      {(values.tags || []).map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          onDelete={() => handleTagDelete(tag)}
                          disabled={loading}
                        />
                      ))}
                    </Box>
                    {tagError && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 1, display: "block" }}
                      >
                        {tagError}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl
                      fullWidth
                      error={touched.categoryId && Boolean(errors.categoryId)}
                    >
                      <InputLabel>Category</InputLabel>
                      <Select
                        name="categoryId"
                        value={values.categoryId}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={loading}
                      >
                        {categoryOptions.length > 0 ? (
                          categoryOptions.map((category) => (
                            <MenuItem
                              key={category._id}
                              value={category._id}
                              sx={{
                                pl: category.level > 0 ? category.level * 2 : 1,
                              }}
                            >
                              {category.title}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem disabled>
                            <em>No categories available</em>
                          </MenuItem>
                        )}
                      </Select>
                      {touched.categoryId && errors.categoryId && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 1, ml: 2 }}
                        >
                          {errors.categoryId}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isTrending}
                          onChange={(e) =>
                            setFieldValue("isTrending", e.target.checked)
                          }
                          disabled={loading}
                        />
                      }
                      label="Trending Product"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      After creating/updating the product, you can add variants
                      and set a default variant to publish the product.
                    </Alert>
                  </Grid>

                  <Grid item xs={12}>
                    <Box display="flex" gap={2} justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={handleCancel}
                        disabled={loading}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        startIcon={
                          loading ? (
                            <CircularProgress size={20} />
                          ) : (
                            <SaveIcon />
                          )
                        }
                        disabled={loading || isSubmitting}
                      >
                        {loading
                          ? "Saving..."
                          : isEdit
                          ? "Update Product"
                          : "Create Product"}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </Paper>
    </Box>
  );
};

export default ProductForm;
